import React, { useState, useMemo } from 'react';
import { PickupGame, SportType } from '../../types';
import { getStateName } from '../../data/usLocations';
import { 
  MapPin, 
  Activity, 
  Trophy, 
  Calendar, 
  Users, 
  ChevronRight, 
  Zap, 
  Megaphone, 
  Sparkles,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  Filter
} from 'lucide-react';

interface LocalActivityFeedProps {
  registeredCity?: string;
  registeredState?: string;
  pickupGames?: PickupGame[];
  selectedSport?: SportType;
  onJoinGame?: (gameId: string) => void;
  onNavigateToTab?: (tab: string) => void;
}

interface LocalNewsItem {
  id: string;
  type: 'tournament' | 'pickup' | 'announcement' | 'court';
  title: string;
  location: string;
  city: string;
  state: string;
  time: string;
  sport: SportType;
  description: string;
  attendeesCount?: number;
  gameId?: string;
  isHot?: boolean;
}

export const LocalActivityFeed: React.FC<LocalActivityFeedProps> = ({
  registeredCity = 'Venice',
  registeredState = 'CA',
  pickupGames = [],
  selectedSport,
  onJoinGame,
  onNavigateToTab
}) => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'tournaments' | 'pickups' | 'alerts'>('all');

  const city = registeredCity || 'Venice';
  const stateCode = registeredState || 'CA';
  const stateName = getStateName(stateCode);

  const activeSport = selectedSport || 'basketball';
  const sportLabel = activeSport.charAt(0).toUpperCase() + activeSport.slice(1);

  // Generate localized activity items based on state, city, and active sport
  const activityItems = useMemo<LocalNewsItem[]>(() => {
    const list: LocalNewsItem[] = [];

    // Map existing pickup games matching active sport (or all if none match)
    const matchingGames = selectedSport 
      ? pickupGames.filter(g => g.sport === selectedSport)
      : pickupGames;

    const gamesToUse = matchingGames.length > 0 ? matchingGames : pickupGames.filter(g => !selectedSport || g.sport === selectedSport);

    gamesToUse.forEach((g) => {
      const gCity = g.courtName || g.address || city;
      list.push({
        id: `game_${g.id}`,
        type: 'pickup',
        title: g.title || `${g.sport.toUpperCase()} Pickup Match`,
        location: `${gCity}, ${stateCode}`,
        city: city,
        state: stateCode,
        time: g.dateTime || 'Today @ 6:00 PM',
        sport: g.sport || activeSport,
        description: `Join ${g.currentPlayers || g.playersList?.length || 4}/${g.maxPlayers || 10} local players checked in at ${g.courtName || 'Local Court'}.`,
        attendeesCount: g.currentPlayers || g.playersList?.length || 4,
        gameId: g.id,
        isHot: true
      });
    });

    if (activeSport === 'volleyball') {
      list.push({
        id: 'vb_tourn_1',
        type: 'tournament',
        title: `${city} Beach Volleyball Pro-Am Quad Open 🏐`,
        location: `${city} Sands Arena Court #1`,
        city: city,
        state: stateCode,
        time: 'This Sunday 10:00 AM',
        sport: 'volleyball',
        description: `4v4 co-ed beach volleyball tournament. Spike competition, radar gun serve tracking & scout scoreboard.`,
        attendeesCount: 36,
        isHot: true
      });
      list.push({
        id: 'vb_alert_1',
        type: 'announcement',
        title: `Sand Volleyball Courts Resurfaced at ${city} Pier!`,
        location: `${city} Ocean Front Park`,
        city: city,
        state: stateCode,
        time: '3 hours ago',
        sport: 'volleyball',
        description: `Parks Department added competition-grade Olympic white sand and high-tension professional antennas.`,
        isHot: false
      });
      list.push({
        id: 'vb_alert_2',
        type: 'announcement',
        title: `${stateName} Volleyball League Season Kickoff ⚡`,
        location: `Statewide (${stateCode})`,
        city: city,
        state: stateCode,
        time: 'Yesterday',
        sport: 'volleyball',
        description: `Season 4 registration is live! Check state leaderboards to secure your division seeding.`,
        isHot: true
      });
    } else if (activeSport === 'soccer') {
      list.push({
        id: 'sc_tourn_1',
        type: 'tournament',
        title: `${city} 7v7 Metro Turf Cup Championship ⚽`,
        location: `${city} Regional Soccer Park`,
        city: city,
        state: stateCode,
        time: 'Saturday 8:30 AM',
        sport: 'soccer',
        description: `16-team double elimination turf cup. Golden boot trophy and $1,200 cash purse.`,
        attendeesCount: 44,
        isHot: true
      });
      list.push({
        id: 'sc_alert_1',
        type: 'announcement',
        title: `Floodlights Extended for Late-Night Soccer Matches`,
        location: `${city} Central Sports Pitch`,
        city: city,
        state: stateCode,
        time: '4 hours ago',
        sport: 'soccer',
        description: `Field lighting now operational until 11:00 PM on Friday and Saturday nights.`,
        isHot: false
      });
      list.push({
        id: 'sc_alert_2',
        type: 'announcement',
        title: `${stateName} State Premier Soccer Qualifier`,
        location: `${city} Waterfront Stadium`,
        city: city,
        state: stateCode,
        time: '2 days ago',
        sport: 'soccer',
        description: `Scouts from regional development academies will be evaluating player profiles on Playground PRO.`,
        isHot: true
      });
    } else if (activeSport === 'pickleball') {
      list.push({
        id: 'pb_tourn_1',
        type: 'tournament',
        title: `${city} Dink & Smash Pickleball Open 🏓`,
        location: `${city} Hub Courts 1-4`,
        city: city,
        state: stateCode,
        time: 'Saturday 10:00 AM',
        sport: 'pickleball',
        description: `Rotational doubles round-robin. 3.5 to 5.0 rating divisions with custom paddle prizes.`,
        attendeesCount: 28,
        isHot: true
      });
      list.push({
        id: 'pb_alert_1',
        type: 'announcement',
        title: `8 New Dedicated Pickleball Courts Opened in ${city}`,
        location: `${city} Community Sports Center`,
        city: city,
        state: stateCode,
        time: '5 hours ago',
        sport: 'pickleball',
        description: `New permanent acrylic courts with low-glare LED lighting open daily from 7:00 AM to 9:00 PM.`,
        isHot: false
      });
    } else {
      // Basketball & general
      list.push({
        id: 'tourn_1',
        type: 'tournament',
        title: `${city} ${stateCode} Summer Slam Championship 🏀`,
        location: `${city} Regional Sports Complex`,
        city: city,
        state: stateCode,
        time: 'Starts Saturday 9:00 AM',
        sport: 'basketball',
        description: `Official ${stateName} state qualifier. 16-team double elimination bracket with $1,500 prize pool.`,
        attendeesCount: 32,
        isHot: true
      });
      list.push({
        id: 'alert_1',
        type: 'announcement',
        title: `New Court Lights Installed in ${city}!`,
        location: `${city} Central Park Courts`,
        city: city,
        state: stateCode,
        time: '2 hours ago',
        sport: 'basketball',
        description: `Parks Department completed LED floodlight installation for night games until 10:00 PM.`,
        isHot: false
      });
      list.push({
        id: 'alert_2',
        type: 'announcement',
        title: `${stateName} Basketball Summer League Draft`,
        location: `Statewide (${stateCode})`,
        city: city,
        state: stateCode,
        time: 'Yesterday',
        sport: 'basketball',
        description: `Verified league scouts are reviewing top XP rankings for upcoming state invites.`,
        isHot: true
      });
    }

    return list;
  }, [pickupGames, selectedSport, activeSport, city, stateCode, stateName]);

  // Filter list based on selected category
  const filteredItems = useMemo(() => {
    if (activeCategory === 'tournaments') return activityItems.filter((i) => i.type === 'tournament');
    if (activeCategory === 'pickups') return activityItems.filter((i) => i.type === 'pickup');
    if (activeCategory === 'alerts') return activityItems.filter((i) => i.type === 'announcement');
    return activityItems;
  }, [activityItems, activeCategory]);

  return (
    <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-950 p-5 md:p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
      {/* Header & City Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3.5">
        <div className="flex items-center space-x-2.5">
          <div className="p-2.5 bg-cyan-400 text-black rounded-2xl shadow-md">
            <Activity className="w-5 h-5 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-cyan-400/20 text-cyan-300 rounded font-mono border border-cyan-400/30">
                LOCAL ACTIVITY FEED
              </span>
              <span className="text-[10px] text-indigo-300 font-mono flex items-center gap-1">
                <MapPin className="w-3 h-3 text-lime-400" />
                Filtered by: {city}, {stateCode} • {sportLabel}
              </span>
            </div>
            <h3 className="text-lg font-black italic uppercase text-white mt-0.5">
              Regional Happenings & {sportLabel} Alerts
            </h3>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap items-center gap-1 bg-black/40 p-1 rounded-2xl border border-white/10">
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition ${
              activeCategory === 'all' ? 'bg-cyan-400 text-black font-extrabold shadow-md' : 'text-indigo-200 hover:text-white'
            }`}
          >
            All Updates
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory('pickups')}
            className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition ${
              activeCategory === 'pickups' ? 'bg-cyan-400 text-black font-extrabold shadow-md' : 'text-indigo-200 hover:text-white'
            }`}
          >
            Pickups
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory('tournaments')}
            className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition ${
              activeCategory === 'tournaments' ? 'bg-cyan-400 text-black font-extrabold shadow-md' : 'text-indigo-200 hover:text-white'
            }`}
          >
            Tournaments
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory('alerts')}
            className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition ${
              activeCategory === 'alerts' ? 'bg-cyan-400 text-black font-extrabold shadow-md' : 'text-indigo-200 hover:text-white'
            }`}
          >
            Alerts
          </button>
        </div>
      </div>

      {/* Activity List */}
      <div className="space-y-3 max-h-96 overflow-y-auto pr-1 custom-scrollbar">
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <div
              key={item.id}
              className="p-4 bg-indigo-900/50 hover:bg-indigo-900/80 rounded-2xl border border-white/10 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 group"
            >
              <div className="space-y-1">
                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                  {item.isHot && (
                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 text-[9px] font-mono font-black uppercase rounded border border-rose-500/30 flex items-center gap-1">
                      <Zap className="w-3 h-3 text-rose-400 fill-rose-400" />
                      HOT
                    </span>
                  )}
                  <span className={`px-2 py-0.5 text-[9px] font-mono font-black uppercase rounded ${
                    item.type === 'tournament' 
                      ? 'bg-amber-400/20 text-amber-300 border border-amber-400/30'
                      : item.type === 'pickup'
                      ? 'bg-lime-400/20 text-lime-300 border border-lime-400/30'
                      : 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/30'
                  }`}>
                    {item.type.toUpperCase()} • {item.sport.toUpperCase()}
                  </span>
                  <span className="text-[10px] text-indigo-300 font-mono flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {item.time}
                  </span>
                </div>

                <h4 className="font-extrabold text-sm text-white group-hover:text-cyan-300 transition flex items-center gap-1.5">
                  <span>{item.title}</span>
                </h4>

                <p className="text-xs text-indigo-200/80 font-medium">
                  {item.description}
                </p>

                <div className="flex items-center space-x-3 text-[10px] text-indigo-300/80 font-mono pt-0.5">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-rose-400" />
                    {item.location}
                  </span>
                  {item.attendeesCount && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-lime-400" />
                      {item.attendeesCount} Players Attending
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="shrink-0 flex items-center space-x-2 self-end sm:self-center">
                {item.type === 'pickup' && item.gameId && onJoinGame && (
                  <button
                    type="button"
                    onClick={() => onJoinGame(item.gameId!)}
                    className="px-3.5 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1"
                  >
                    <span>Join Match</span>
                  </button>
                )}

                {item.type === 'tournament' && onNavigateToTab && (
                  <button
                    type="button"
                    onClick={() => onNavigateToTab('tournaments')}
                    className="px-3.5 py-1.5 bg-amber-400 hover:bg-amber-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1"
                  >
                    <span>View Bracket</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                )}

                {item.type === 'announcement' && onNavigateToTab && (
                  <button
                    type="button"
                    onClick={() => onNavigateToTab('pickup_games')}
                    className="px-3.5 py-1.5 bg-indigo-800 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl border border-white/10 transition flex items-center space-x-1"
                  >
                    <span>Court Info</span>
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 bg-indigo-950/60 rounded-2xl border border-white/10 p-4 space-y-2">
            <Megaphone className="w-8 h-8 text-indigo-400/50 mx-auto" />
            <p className="text-xs text-indigo-200/70 font-semibold">
              No regional updates found for category "{activeCategory}" in {city}, {stateCode}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
